import React, { createContext, useContext, useMemo } from "react";
import { organizationEndpoints } from "../api/apiEndpoints";
import useFetch from "../utils/hooks/useFetch";
import { userEndpoints } from "../api/endpoints/user.endpoints";

// Create the context
export const OrgContext = createContext();

export default function useOrganizationContext() {
  return useContext(OrgContext);
}

export const OrgContextProvider = ({ children }) => {
  // Use the useFetch hook to fetch organization data
  const {
    data,
    loading,
    error,
    refetch: refetchOrg,
  } = useFetch(organizationEndpoints.getOrganization);
  const {
    data: userData,
    loading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useFetch(userEndpoints.getUser);

  const displayOrganization = useMemo(() => {
    const name = data?.name || userData?.organization?.name || userData?.name || "Organization";
    const logo = data?.logo || userData?.organization?.logo || "";
    return { name, logo };
  }, [data, userData]);

  const organizationId = useMemo(
    () => data?.id || userData?.organization?.id || null,
    [data?.id, userData?.organization?.id],
  );

  const displayUser = useMemo(() => {
    const name = userData?.name || (userData?.email ? userData.email.split("@")[0] : "") || "User";
    const role = userData?.role
      ? `${userData.role.charAt(0).toUpperCase()}${userData.role.slice(1)}`
      : "Admin";
    return {
      name,
      role,
      profileImage: userData?.profileImage || "",
      email: userData?.email || "",
    };
  }, [userData]);

  // Render the children components and provide the organization data through context
  return (
    <OrgContext.Provider
      value={{
        data,
        organizationId,
        loading,
        error,
        userData,
        displayUser,
        displayOrganization,
        refetchOrg,
        refetchUser,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};
